/**
 * Places the drive can start from.
 *
 * The alert cron has no phone GPS, so it needs a real origin saved on the server. These are the
 * two it is normally one of; coordinates came from the Google Geocoding API rather than being
 * read off a map, so they match the addresses exactly.
 */

import { distanceMeters } from "./time";

export interface OriginPreset {
	id: string;
	label: string;
	/** Longer wording where the destination card has room for it. */
	onwardLabel?: string;
	/** Reads as "from the Richmond" beside a commute. */
	shortLabel: string;
	address: string;
	lat: number;
	lon: number;
}

export const ORIGIN_PRESETS: OriginPreset[] = [
	{
		id: "richmond",
		label: "Richmond Dist",
		onwardLabel: "Richmond District",
		shortLabel: "the Richmond",
		address: "3031A Balboa St, San Francisco, CA 94121",
		lat: 37.7758217,
		lon: -122.4912388,
	},
	{
		id: "twin-peaks",
		label: "Twin Peaks",
		shortLabel: "Twin Peaks",
		address: "535 Burnett Ave, San Francisco, CA 94131",
		lat: 37.7526747,
		lon: -122.4446979,
	},
];

/** Each commute starts somewhere specific, so choosing one fills in the other. */
const ORIGIN_BY_PROFILE: Record<string, string> = {
	"civic-am": "richmond",
	"glen-am": "twin-peaks",
};

/**
 * Places to stand while testing, in journey order for each commute.
 *
 * Both houses, both stations, and a point along each drive. The two en-route ones sit on their
 * corridor deliberately: a point off it would change which commute the app thinks this is, and
 * then the thing under test is not the thing being tested. The last three are the ride home,
 * which starts at school and walks down to Rockridge.
 */
export const SIMULATED_LOCATIONS: Array<{ id: string; label: string; lat: number; lon: number }> = [
	{ id: "balboa", label: "Balboa", lat: 37.7758217, lon: -122.4912388 },
	{ id: "fulton-divisadero", label: "Fulton & Divisadero", lat: 37.7761, lon: -122.439 },
	{ id: "civic-center", label: "Civic Center", lat: 37.779732, lon: -122.414123 },
	{ id: "twin-peaks", label: "Twin Peaks", lat: 37.7526747, lon: -122.4446979 },
	{ id: "diamond-heights", label: "Diamond Heights", lat: 37.7455, lon: -122.441 },
	{ id: "glen-park", label: "Glen Park", lat: 37.733064, lon: -122.433817 },
	{ id: "transbay-tunnel", label: "Transbay Tunnel", lat: 37.7988, lon: -122.3458 },
	{ id: "college-prep", label: "College Prep", lat: 37.8482199, lon: -122.2397897 },
	{ id: "ivanhoe-miles", label: "Ivanhoe & Miles", lat: 37.8474864, lon: -122.2466475 },
	{ id: "rockridge", label: "Rockridge", lat: 37.844601, lon: -122.251793 },
];

export function defaultOriginFor(profileId: string): OriginPreset | null {
	const id = ORIGIN_BY_PROFILE[profileId];
	return ORIGIN_PRESETS.find((preset) => preset.id === id) ?? null;
}

/** Tight enough that only the saved preset itself matches, not the next street over. */
const MATCH_TOLERANCE_DEGREES = 0.0005;

export function matchOriginPreset(origin: { lat: number; lon: number } | null): OriginPreset | null {
	if (!origin) {
		return null;
	}
	return (
		ORIGIN_PRESETS.find(
			(preset) => Math.abs(preset.lat - origin.lat) < MATCH_TOLERANCE_DEGREES && Math.abs(preset.lon - origin.lon) < MATCH_TOLERANCE_DEGREES,
		) ?? null
	);
}

/** What to call the spot he is pretending to stand in. Coordinates when it is not one of ours. */
export function simulatedLocationLabel(position: { lat: number; lon: number } | null): string | null {
	if (!position) {
		return null;
	}
	const place = SIMULATED_LOCATIONS.find(
		(entry) => Math.abs(entry.lat - position.lat) < MATCH_TOLERANCE_DEGREES && Math.abs(entry.lon - position.lon) < MATCH_TOLERANCE_DEGREES,
	);
	return place?.label ?? `${position.lat.toFixed(4)}, ${position.lon.toFixed(4)}`;
}

/**
 * The immediate home area. Retained stationary fixes at both homes stayed within 47 m, while a
 * 50 m boundary detects departure on the first supported fix after the car leaves that envelope.
 */
export const HOME_GEOFENCE_METERS = 50;

/** Which house the phone is standing at, if any — not which one the commute starts from. */
export function homeAt(position: { lat: number; lon: number } | null): OriginPreset | null {
	if (!position) {
		return null;
	}
	return ORIGIN_PRESETS.find((preset) => distanceMeters(preset, position) <= HOME_GEOFENCE_METERS) ?? null;
}

/**
 * Far enough from a known address to mean he has gone, close enough to notice by the end of the
 * street. The home fence excludes observed stationary drift, and the accuracy of the fix itself
 * also has to support the claim.
 */
export const LEFT_ORIGIN_METERS = 50;

/**
 * Whether this fix says the drive has started.
 *
 * Here rather than in the page because the page is the one thing that cannot be relied on to ask.
 * On 2026-08-31 two phones rode in the same car: the one in the foreground called it 55 seconds
 * after the wheels turned, and the one behind Waze took seven and a half minutes — long enough for
 * the cron to send a reminder to leave to a man already a mile down the road. The fence was never
 * the problem; nothing awake was running it. The server runs this against the fixes the shell posts
 * while the page sleeps, and it must be the same rule or the two would disagree about the morning.
 *
 * Standing in either house forbids it outright, whichever one the commute starts from: the saved
 * starting point can change without the phone moving an inch, and distance alone is blind to that.
 */
export function hasLeftOrigin(
	origin: { lat: number; lon: number } | null,
	position: { lat: number; lon: number } | null,
	accuracyMeters: number | null,
): boolean {
	if (!origin || !position || homeAt(position) != null) {
		return false;
	}
	return distanceMeters(origin, position) >= Math.max(LEFT_ORIGIN_METERS, (accuracyMeters ?? 0) * 2);
}

/**
 * The house each city station is the way home to.
 *
 * The morning already pairs them — Civic Center is the Richmond's station and Glen Park is Twin
 * Peaks' — so the ride back is that same pairing read the other way round.
 */
const HOME_BY_STATION: Record<string, string> = {
	CIVC: "richmond",
	GLEN: "twin-peaks",
	// SFO is not really "home" for anyone, but a Richmond binding is a harmless default for a
	// station that never needs a pickup — chooseOnward's reverse lookup still resolves "richmond" to
	// CIVC first, so this does not change which house the toggle offers there.
	SFIA: "richmond",
};

/**
 * Where home is, coming back.
 *
 * The station he is getting off at settles it, because that is a choice he has already made
 * rather than something to infer. Only with no station to go on does the nearest house answer,
 * and that is a starting point he can override rather than a decision made for him.
 */
export function homeForStation(stationId: string | null, position: { lat: number; lon: number } | null = null): OriginPreset | null {
	const paired = stationId ? ORIGIN_PRESETS.find((preset) => preset.id === HOME_BY_STATION[stationId]) : null;
	if (paired) {
		return paired;
	}
	if (!position) {
		return null;
	}
	return [...ORIGIN_PRESETS].sort((a, b) => distanceMeters(a, position) - distanceMeters(b, position))[0] ?? null;
}

/** The city station that house is served by, which is the same pairing read backwards. */
export function stationForHome(presetId: string | null): string | null {
	if (!presetId) {
		return null;
	}
	return Object.keys(HOME_BY_STATION).find((stationId) => HOME_BY_STATION[stationId] === presetId) ?? null;
}

/**
 * Under the bay, which can only mean aboard a train.
 *
 * Matched to the simulated pin rather than fenced off a radius: there is no satellite down there,
 * so a real fix never lands here, and a wide circle over the water would only risk reading a walk
 * along the Embarcadero as a ride.
 */
export function inTransbayTube(position: { lat: number; lon: number } | null): boolean {
	if (!position) {
		return false;
	}
	const tube = SIMULATED_LOCATIONS.find((entry) => entry.id === "transbay-tunnel")!;
	return Math.abs(tube.lat - position.lat) < MATCH_TOLERANCE_DEGREES && Math.abs(tube.lon - position.lon) < MATCH_TOLERANCE_DEGREES;
}
